import React, { useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import SwapVertIcon from "@mui/icons-material/SwapVert";

import { FilterDropdown, FilterChipRow } from "/imports/ui/components/shared";
import { BOOKING_SCOPE_OPTIONS, BOOKING_STATUS_OPTIONS, BOOKING_SORT_OPTIONS, BOOKING_TYPE_OPTIONS } from "../dashboardConstants";

function SortMenuButton({ sort, onSortChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const active = sort === "recent";

  const pick = (value) => {
    setAnchorEl(null);
    onSortChange(value);
  };

  return (
    <>
      <IconButton
        aria-label="Sort bookings"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          flexShrink: 0,
          width: "2.5rem",
          height: "2.5rem",
          borderRadius: 1,
          border: active ? 1.5 : 1,
          borderColor: active ? "primary.main" : "divider",
          color: active ? "primary.main" : "text.secondary",
          bgcolor: (t) => (active ? alpha(t.palette.primary.main, 0.08) : "transparent")
        }}
      >
        <SwapVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { mt: 0.5 } }}
      >
        {BOOKING_SORT_OPTIONS.map((o) => (
          <MenuItem key={o.value} selected={o.value === sort} onClick={() => pick(o.value)}>
            <ListItemIcon sx={{ minWidth: "1.75rem" }}>{o.value === sort && <CheckIcon fontSize="small" color="primary" />}</ListItemIcon>
            {o.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function BookingFilters({
  scope,
  status,
  type,
  sort,
  onScopeChange,
  onStatusChange,
  onTypeChange,
  onSortChange,
  hasWaitlists = false,
  availableTypes = []
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Waitlist only shows for a client who is on one, so clubs without class
  // waitlists never see it. Status and sort describe bookings, not waitlists.
  const isWaitlist = scope === "waitlist";
  const scopeOptions = hasWaitlists || isWaitlist ? [...BOOKING_SCOPE_OPTIONS, { value: "waitlist", label: "Waitlist" }] : BOOKING_SCOPE_OPTIONS;

  // A client with one kind of booking has nothing to filter by, so the type filter only
  // appears with two or more. A type still selected from a link stays listed so it can
  // be cleared. The waitlist is classes only.
  const typeOptions = BOOKING_TYPE_OPTIONS.filter((o) => availableTypes.includes(o.value) || o.value === type);
  const showTypes = !isWaitlist && (typeOptions.length > 1 || Boolean(type));

  // On phones the loose scope/status chip rows read as one undifferentiated
  // blob. Scope becomes a full-width segmented control (the primary axis) and
  // status/sort collapse into two compact dropdowns on a single row.
  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={scope}
          onChange={(_e, v) => v && onScopeChange(v)}
          sx={{
            "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, py: 0.75, px: 0.5, fontSize: "0.8125rem", whiteSpace: "nowrap" },
            "& .MuiToggleButton-root.Mui-selected": {
              bgcolor: "primary.main",
              color: "common.white",
              "&:hover": { bgcolor: "primary.dark" }
            }
          }}
        >
          {scopeOptions.map((o) => (
            <ToggleButton key={o.value} value={o.value}>
              {o.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ display: isWaitlist ? "none" : "flex", gap: 1 }}>
          <FilterDropdown
            options={BOOKING_STATUS_OPTIONS}
            value={status}
            onChange={onStatusChange}
            placeholder="All statuses"
            fullWidth
            minWidth={0}
            active={Boolean(status)}
          />
          {showTypes && (
            <FilterDropdown
              options={typeOptions}
              value={type}
              onChange={onTypeChange}
              placeholder="All types"
              fullWidth
              minWidth={0}
              active={Boolean(type)}
            />
          )}
          {/* Three dropdowns can't fit their labels on a phone; sort is the one clients
              rarely touch, so it shrinks to an icon when the type filter is showing. */}
          {showTypes ? (
            <SortMenuButton sort={sort} onSortChange={onSortChange} />
          ) : (
            <FilterDropdown
              options={BOOKING_SORT_OPTIONS}
              value={sort}
              onChange={(v) => onSortChange(v || "date")}
              allowAll={false}
              fullWidth
              minWidth={0}
              active={sort === "recent"}
            />
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: 2 }}>
      <FilterChipRow options={scopeOptions} value={scope} onSelect={onScopeChange} />
      {!isWaitlist && (
        <>
          <Box sx={{ width: 0, height: "1.25rem", borderLeft: 1, borderColor: "divider", display: { xs: "none", sm: "block" } }} />
          <FilterChipRow options={BOOKING_STATUS_OPTIONS} value={status} onSelect={onStatusChange} allowClear />
          {showTypes && (
            <>
              <Box sx={{ width: 0, height: "1.25rem", borderLeft: 1, borderColor: "divider", display: { xs: "none", sm: "block" } }} />
              <FilterChipRow options={typeOptions} value={type} onSelect={onTypeChange} allowClear />
            </>
          )}
          <Box sx={{ ml: { sm: "auto" } }}>
            <FilterDropdown
              options={BOOKING_SORT_OPTIONS}
              value={sort}
              onChange={(v) => onSortChange(v || "date")}
              allowAll={false}
              active={sort === "recent"}
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default BookingFilters;
